import { useMemo } from 'react';
import HistoryTime from 'components/HistoryTime';
import { OrganizationTreeSelect } from 'components/OrganizationTreeSelect';
import { getText } from 'i18n/intl';
import _ from 'lodash';
import { rolesSelector } from 'slices/roles.slice';
import { useAppSelector } from 'slices/store';
import { timeRulesSelector } from 'slices/timeRule.slice';
import type { KeyValueCallback } from 'types/utils.type';
import {
  EDefaultStatus,
  ETemplateType,
  type TOrganizationTreeNode,
  type TReservationTemplate,
} from '@timeedit/types';
import { TEInput } from '@timeedit/ui-components';
import './TemplateInformation.scss';

interface TemplateInformationProps {
  template: TReservationTemplate;
  onChange: KeyValueCallback<TReservationTemplate>;
  isExpand?: boolean;
}

type TTreeSelectOption = {
  value: string;
  label: string;
  children?: Array<TTreeSelectOption>;
};

const organizationTreeNodeToTreeSelectOption = (
  node: TOrganizationTreeNode
): TTreeSelectOption => ({
  value: node.virtualId,
  label: node.name,
  children: node.children?.map(organizationTreeNodeToTreeSelectOption),
});

const TemplateInformation = (props: TemplateInformationProps) => {
  const { template, onChange, isExpand } = props;
  const timeRules = useAppSelector(timeRulesSelector);
  const roles = useAppSelector(rolesSelector);

  const timeRuleOptions = useMemo(
    () =>
      timeRules.map((rule) => ({
        value: String(rule.id),
        label: rule.name,
      })),
    [timeRules]
  );

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: String(role.id),
        label: role.name,
      })),
    [roles]
  );

  const statusOptions = useMemo(
    () =>
      Object.values(EDefaultStatus).map((status) => ({
        value: status,
        label: status,
      })),
    []
  );

  return (
    <div className="section-wrapper template-information">
      <div
        className={isExpand ? 'section-col col1 expand' : 'section-col col1'}
      >
        <TEInput.PureText
          value={getText(template.templateType)}
          label="Template type"
          tooltip="Template type"
          data-testid="template-type"
        />
        <TEInput.Input
          value={template.name}
          onChange={(value) => onChange('name', value)}
          label="Name"
          tooltip="Name"
          placeholder="New Reservation Template"
          data-testid="resv-name"
        />
        <TEInput.TextArea
          value={template.documentation}
          onChange={(value) => onChange('documentation', value)}
          label="Description"
          tooltip="Description"
          data-testid="description"
          placeholder="Some description"
        />
        <TEInput.Select
          options={timeRuleOptions}
          value={(template.timeRule && String(template.timeRule)) || undefined}
          onChange={(val) => onChange('timeRule', !val ? 0 : Number(val))}
          label="Time rule"
          tooltip="Time rule"
          placeholder="Select"
          data-testid="time-rule"
        />
      </div>
      <div
        className={isExpand ? 'section-col col2 expand' : 'section-col col2'}
      >
        <TEInput.Switch
          checked={template.active}
          onChange={(val) => onChange('active', val)}
          label="Active"
          tooltip="Active"
          data-testid="resv-active"
        />
        <TEInput.Switch
          checked={template.affectOrders}
          onChange={(val) => onChange('affectOrders', val)}
          label="Affect orders"
          tooltip="Affect orders"
          disabled={template.templateType !== ETemplateType.RESERVATION}
          data-testid="affect-order"
        />
        {/* If template type is not reservation, then deleteWhenCulling value is always true */}
        <TEInput.Switch
          checked={
            template.templateType !== ETemplateType.RESERVATION
              ? true
              : template.deleteWhenCulling
          }
          onChange={(val) => onChange('deleteWhenCulling', val)}
          label="Delete reserv. when culling"
          tooltip="Delete reserv. when culling"
          disabled={template.templateType !== ETemplateType.RESERVATION}
          data-testid="delete-once-culling"
        />
        <TEInput.PureText value={`${template.id}`} label="Id" tooltip="Id" />
        <TEInput.PureText
          value={_.get(template, ['maxNumberOfObjects'], 0)}
          label="Max objects"
          tooltip="Max objects"
        />
      </div>
      <div
        className={isExpand ? 'section-col col3 expand' : 'section-col col3'}
      >
        <TEInput.Select
          options={roleOptions}
          value={
            (template.privateRole && String(template.privateRole)) || undefined
          }
          onChange={(val) => onChange('privateRole', (val && Number(val)) || 0)}
          label="Private role"
          tooltip="Private role"
          placeholder="Select"
          data-testid="private-role"
        />
        <OrganizationTreeSelect
          value={
            template.standardOrganization
              ? String(template.standardOrganization)
              : ''
          }
          onChange={(val) => {
            onChange('standardOrganization', val);
          }}
          onlyLeafNodes
          allowClear
          label="Standard organization"
          tooltip="Only use org node that is under the parent node “oReservations”, for example “Reservation - Scheduling"
          placeholder="Select"
          data-testid="standard-org"
        />
        <TEInput.Select
          options={statusOptions}
          value={template.defaultStatus}
          onChange={(val) => onChange('defaultStatus', val)}
          label="Default status"
          tooltip="Default status"
          placeholder="Select"
          data-testid="default-stt"
        />
        <HistoryTime history={template?.history} />
      </div>
    </div>
  );
};

export default TemplateInformation;
